import { useTunnusContext } from 'contexts/TunnusContext';
import { useUILanguage } from 'hooks/useUILanguage';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { apmRum } from 'services/apm-rum';
import { teardown } from 'services/braze';
import logger from 'services/logger';
import { BrazeScriptLoader } from './BrazeScriptLoader';
import { useBrazeToken } from './useBrazeToken';
import type { BrazeContentCard, BrazeSdk } from './types';

type BrazeSdkContextType = {
  braze: BrazeSdk | null;
  contentCards: BrazeContentCard[];
};

const BrazeSdkContext = React.createContext<BrazeSdkContextType>({
  braze: null,
  contentCards: [],
});

type BrazeSdkProviderProps = {
  children: React.ReactNode;
};

export const BrazeSdkProvider: React.FC<BrazeSdkProviderProps> = ({
  children,
}) => {
  const language = useUILanguage();
  const { user: { userId } = {} } = useTunnusContext();

  const [brazeInstance, setBrazeInstance] = useState<BrazeSdk | null>(null);
  const [contentCards, setContentCards] = useState<BrazeContentCard[]>([]);

  const { token, refreshToken } = useBrazeToken();

  const isBrazeAllowed = !!userId;

  // Teardown SDK when Braze is no longer allowed
  useEffect(() => {
    if (!isBrazeAllowed && brazeInstance) {
      try {
        teardown(brazeInstance);
        setBrazeInstance(null);
      } catch (error) {
        logger.error(error, 'Failed to teardown Braze SDK');
        if (error instanceof Error) apmRum?.captureError(error);
      }
    }
  }, [brazeInstance, isBrazeAllowed]);

  // Pass changed token to the SDK
  useEffect(() => {
    if (!isBrazeAllowed) {
      return;
    }

    if (brazeInstance && token) {
      brazeInstance.setSdkAuthenticationSignature(token);
    }
  }, [brazeInstance, isBrazeAllowed, token]);

  const contextValue = useMemo(
    () => ({ braze: brazeInstance, contentCards }),
    [brazeInstance, contentCards]
  );

  return (
    <BrazeSdkContext.Provider value={contextValue}>
      {isBrazeAllowed && token && userId ? (
        <BrazeScriptLoader
          language={language}
          refreshToken={refreshToken}
          setContentCards={setContentCards}
          token={token}
          userId={userId}
          onInit={setBrazeInstance}
        />
      ) : null}

      {children}
    </BrazeSdkContext.Provider>
  );
};

export function useBrazeSdk(): BrazeSdkContextType {
  return useContext(BrazeSdkContext);
}
